/**
 * The client menu, as one rule: what is on it, in what state, and why.
 *
 * Owner, 22–24 Sep 2026, from two mock-ups ("The client menu, today and proposed" and "The same
 * menu, at four points in a client's life"). The menu shows what is known and nothing more, and
 * grows as the file does — the same stages the Partner Portal's pipeline already uses:
 *
 *   1. Invite sent      — no name yet ("Not yet named"), the profile steps, and a hint saying the
 *                         living options appear once the goals in step 3 are set.
 *   2. Filling in       — step 2 has given the file a name; the goals ticked in step 3 appear as
 *                         living options, each a single click from anywhere.
 *   3. Ready to model   — the profile is complete, so its steps fold away behind a "Complete" tag.
 *   4. Advice           — plans exist, so their count sits on the option they belong to, and
 *                         Reports appears for the first time because there is now something to open.
 *
 * Decisions the owner made that this file exists to keep:
 *   - The client-file block carries the NAME and nothing else (23 Sep: "Just keep it simple with
 *     the client file name").
 *   - Only the goals ticked are listed. The other options are one line away under Goals, and the
 *     hint says so, rather than clutter for a client who never asked about them.
 *   - NO LOCK (24 Sep). A first design held each option back until Assets and Income/Expenses were
 *     done; the Dashboard has no such gate, and the owner did not want one while testing ("I don't
 *     want to be in a scenario where I need to enter every single bit of data before it unlocks").
 *     The menu and the Dashboard are two doors to the same rooms, reading the same facts, and they
 *     must never disagree about which doors are open.
 *   - Nothing on the menu leads to "coming soon". Partners is gone until there is something behind
 *     it; Reports opens the advice documents on the Dashboard, and only once one exists.
 *
 * "Complete" is profileCompleteness.ts's word — the same seven steps the Partner Portal measures —
 * so the menu and the portal never disagree about whether a file is ready.
 */
import { evaluateProfileCompleteness, type FactFindStepId } from '../../../../shared/src/factFind/profileCompleteness.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';

/** The least a saved plan has to carry for the menu to count it (Dashboard's SavedStrategy has more). */
export interface MenuPlan { type?: string }
/** The least an advice document has to carry for the menu to count it (Dashboard's SavedCaseStudy has more). */
export interface MenuCaseStudy { caseId?: string; soaDeliveryStatus?: string }

export interface ClientMenuInput {
    factFind: FactFind | null | undefined;
    savedStrategies?: readonly MenuPlan[];
    savedCaseStudies?: readonly MenuCaseStudy[];
    /** An adviser's client file: only the advice documents raised on this case count (as the Dashboard filters). */
    professionalCaseId?: string | null;
    /** A name known before the profile has one — the Partner Portal's case name — or nothing. */
    fallbackName?: string | null;
}

/** The profile's eight steps as the UI shows them. Step 1 has no completeness of its own (see profileCompleteness.ts). */
export const PROFILE_STEPS: ReadonlyArray<{ step: number; name: string; id: FactFindStepId | null }> = [
    { step: 1, name: "Who's Completing", id: null },
    { step: 2, name: 'Your Details', id: 'your_details' },
    { step: 3, name: 'Goals', id: 'goals' },
    { step: 4, name: 'Living Arrangements', id: 'living_arrangements' },
    { step: 5, name: 'Assets', id: 'assets' },
    { step: 6, name: 'Liabilities', id: 'liabilities' },
    { step: 7, name: 'Income/Expenses', id: 'income_expenses' },
    { step: 8, name: 'Family/Estate', id: 'family_estate' },
];

export const GOALS_STEP = 3;

export interface LivingOptionDef {
    /** The goal in the fact-find's goals bag that lists this option. Null for an option with no goal of its own. */
    goal: 'aged_care' | 'home_care' | 'retirement_village' | 'downsize_home' | 'granny_flat' | null;
    /** The app page (App.tsx's renderActivePage) — the same door the Dashboard opens. */
    page: string;
    label: string;
    /** The `type` a saved plan of this module carries (Dashboard/index.tsx counts them the same way). */
    planType: string;
}

/** In the order the profile offers the goals (ClientProfileSteps.tsx, step 3). */
export const LIVING_OPTIONS: readonly LivingOptionDef[] = [
    { goal: 'aged_care', page: 'AgedCarePage', label: 'Aged care', planType: 'aged_care' },
    { goal: 'home_care', page: 'HomeCarePage', label: 'Support at home', planType: 'home_care' },
    { goal: 'retirement_village', page: 'RetirementVillagePage', label: 'Retirement village', planType: 'retirement_village' },
    { goal: 'downsize_home', page: 'DownsizingPage', label: 'Downsizing', planType: 'downsizer' },
    { goal: 'granny_flat', page: 'GrannyFlatPage', label: 'Granny flat', planType: 'granny_flat' },
];

/**
 * No goal of its own — its only door is Support at Home — so it is listed once a plan of its kind
 * exists, exactly as the Dashboard shows its shelf.
 */
export const CASHFLOW_OPTION: LivingOptionDef = { goal: null, page: 'CashflowPensionPage', label: 'Cashflow & pension', planType: 'cashflow_pension' };

/** The Dashboard section Reports opens — the advice documents. Dashboard/index.tsx puts the id on it. */
export const REPORTS_SECTION_ID = 'advice-documents';

export interface MenuStep { step: number; name: string; done: boolean }
export interface MenuOption extends LivingOptionDef { planCount: number; planTag: string | null }

export interface ClientMenuModel {
    /** The client file's name, or null when nothing has named it yet. */
    fileName: string | null;
    profileComplete: boolean;
    steps: MenuStep[];
    /** The goals ticked, in profile order, plus Cashflow & pension once it has plans. */
    livingOptions: MenuOption[];
    /** One line under the options (or under the profile when there are none) saying what would add more. */
    livingHint: string | null;
    /** Null until an advice document exists — the line is absent, not disabled. */
    reports: { total: number; drafts: number; tag: string } | null;
}

const clean = (v: unknown): string => String(v ?? '').trim();

/**
 * "Joe Smith", "Joe & Kim Smith", "Joe Smith & Kim Jones" — or null until step 2 names the client.
 * The fallback (the Partner Portal's case name) is used only when the profile has no name at all.
 */
export function clientFileName(fd: FactFind | null | undefined, fallbackName?: string | null): string | null {
    const cf = clean(fd?.client?.firstName);
    const cl = clean(fd?.client?.lastName);
    if (!cf) return clean(fallbackName) || null;
    const pf = clean(fd?.partner?.firstName);
    const pl = clean(fd?.partner?.lastName);
    const clientName = [cf, cl].filter(Boolean).join(' ');
    if (fd?.adviceFor !== 'couple' || !pf) return clientName;
    if (cl && pl && cl.toLowerCase() === pl.toLowerCase()) return `${cf} & ${pf} ${cl}`;
    if (!cl && !pl) return `${cf} & ${pf}`;
    return `${clientName} & ${[pf, pl].filter(Boolean).join(' ')}`;
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export function buildClientMenu(input: ClientMenuInput): ClientMenuModel {
    const fd = input.factFind ?? null;
    const completeness = evaluateProfileCompleteness(fd);
    const doneById = new Map(completeness.steps.map((s) => [s.id, s.complete]));

    const steps: MenuStep[] = PROFILE_STEPS.map((s) => ({
        step: s.step,
        name: s.name,
        // Step 1 always holds an answer, so it reads as done once anything at all has been entered.
        done: s.id ? Boolean(doneById.get(s.id)) : !completeness.isEmpty,
    }));

    const goals = (fd?.goals ?? {}) as Record<string, unknown>;
    const plans = input.savedStrategies ?? [];
    const countPlans = (planType: string): number => plans.filter((p) => p?.type === planType).length;
    const withCount = (def: LivingOptionDef): MenuOption => {
        const planCount = countPlans(def.planType);
        return { ...def, planCount, planTag: planCount > 0 ? plural(planCount, 'plan', 'plans') : null };
    };

    const ticked = LIVING_OPTIONS.filter((o) => o.goal && goals[o.goal] === true).map(withCount);
    const cashflow = withCount(CASHFLOW_OPTION);
    const livingOptions = cashflow.planCount > 0 ? [...ticked, cashflow] : ticked;

    let livingHint: string | null = null;
    if (ticked.length === 0) {
        livingHint = `Living options appear once the goals in step ${GOALS_STEP} are set.`;
    } else {
        const others = LIVING_OPTIONS.filter((o) => o.goal && goals[o.goal] !== true).map((o) => o.label.toLowerCase());
        if (others.length) livingHint = `Other options — ${others.join(', ')} — are added under Goals.`;
    }

    const visibleDocs = (input.savedCaseStudies ?? []).filter(
        (s) => !input.professionalCaseId || !s.caseId || s.caseId === input.professionalCaseId,
    );
    const drafts = visibleDocs.filter((s) => (s.soaDeliveryStatus || 'draft') === 'draft').length;
    const reports = visibleDocs.length
        ? { total: visibleDocs.length, drafts, tag: drafts > 0 ? plural(drafts, 'draft', 'drafts') : plural(visibleDocs.length, 'saved', 'saved') }
        : null;

    return {
        fileName: clientFileName(fd, input.fallbackName),
        profileComplete: completeness.isComplete,
        steps,
        livingOptions,
        livingHint,
        reports,
    };
}
